<template>
  <app-page
    :title="pageTitle"
    :subtitle="pageSubtitle"
    :previous="[{ label: 'Recognition', to: { name: 'recognition' } }]"
  >
    <template #below-header>
      <p v-if="shouldShowCompanyIndicator" class="company-note">
        This award will be created for <active-company-pill />
      </p>
    </template>

    <template #custom-content>
      <b-col>
        <ValidationObserver v-slot="v">
          <b-form id="create-award-form" @submit.prevent="v.handleSubmit(onSubmit)">
            <div class="form-container">
              <!-- Basic Details -->
              <basic-details-section
                :name="awardForm.name"
                :scope="scope"
                :scope-fields="scopeFields"
                :require-explicit-scope="requiresExplicitDivisionScope"
                :scope-error="divisionScopeError"
                @update:name="awardForm.name = $event"
                @update:scope="onScopeChange"
              >
                <template #after-divisions>
                  <hr />

                  <div class="company-values-container">
                    <div class="label-row">
                      <div class="label">
                        Add your company values (optional)
                        <small>
                          Tag relevant values to help employees understand what behaviours this award recognises. Add
                          the values that matter to your organisation.
                        </small>
                      </div>
                      <b-form-checkbox v-model="companyValuesEnabled" switch />
                    </div>
                    <div v-if="companyValuesEnabled">
                      <create-company-values-modal v-model="companyValues" />
                    </div>
                  </div>

                  <hr />

                  <div class="short-description-container">
                    <div class="label">
                      Short description (optional)
                      <small>
                        A short description with extra detail about this award — for example its location or budget
                        source. In the Stream app this is only visible to approvers and admins, not to members.
                      </small>
                    </div>
                    <app-input
                      v-model="shortDescription"
                      type="textarea"
                      :rows="2"
                      maxlength="500"
                      placeholder="Add a short description (optional)"
                    />
                  </div>
                </template>
              </basic-details-section>

              <!-- Appearance -->
              <image-upload-section
                :banner-images="bannerImages"
                :banner-images-error="bannerImagesError"
                :badge-file="badgeFile"
                :badge-preview-url="badgePreviewUrl"
                :allowed-image-types="allowedImageTypes"
                :max-size-bytes="MAX_BANNER_SIZE_BYTES"
                @add:bannerImages="addBannerImages"
                @add:pendingBannerFiles="addPendingBannerFiles"
                @remove:bannerImage="removeBannerImage"
                @update:badgeFile="onBadgeFileUpdate"
                @update:badgePreviewUrl="onBadgePreviewUrlUpdate"
                @gallerySelect:badge="onBadgeGallerySelect"
              />

              <!-- Award Details -->
              <div>
                <h4>Award details</h4>
                <sme-card large>
                  <div class="card-container">
                    <div class="label-row">
                      <div class="label">
                        Enable badges (optional)
                        <small>Turn awards into collectible badges that appear on recipients' home page.</small>
                      </div>
                      <b-form-checkbox v-model="badgesEnabled" switch />
                    </div>

                    <div class="label-row">
                      <div class="label">
                        Let the sender choose the image
                        <small>
                          When on, the person giving this award picks which banner image is shown. Requires at least two
                          images.
                        </small>
                      </div>
                      <b-form-checkbox v-model="allowNominatorImageChoice" switch :disabled="bannerImages.length < 2" />
                    </div>

                    <monetary-reward-section
                      :enabled="monetaryRewardEnabled"
                      :currency-symbol="currencySymbol"
                      :amounts="awardValueMinor"
                      :default-amount-minor="defaultAwardValueMinor"
                      :amount-error="awardValueError"
                      :redemption-options="selectedRedemptionOptions"
                      :redemption-error="redemptionSelectionError"
                      :expiration-months="expirationMonths"
                      @update:enabled="monetaryRewardEnabled = $event"
                      @update:amounts="awardValueMinor = $event"
                      @update:defaultAmountMinor="onDefaultAwardValueMinorChange"
                      @update:redemptionOptions="selectedRedemptionOptions = $event"
                      @update:expirationMonths="expirationMonths = $event"
                    >
                      <template #reward-amounts>
                        <div class="amount-mode-container">
                          <b-form-radio v-model="amountMode" name="amount-mode" value="fixed">
                            Set fixed reward amount(s)
                          </b-form-radio>
                          <b-form-radio v-model="amountMode" name="amount-mode" value="free">
                            Set adjustable reward amount
                          </b-form-radio>
                        </div>

                        <div v-if="amountMode === 'fixed'" class="reward-amount-container">
                          <div class="label">
                            Reward amount options
                            <small>
                              Choose one or more values that can be awarded with this prize. If you select multiple, the
                              person suggesting a reward can choose the amount.
                            </small>
                          </div>
                          <create-reward-amounts-modal
                            :value="awardValueMinor"
                            :default-minor="defaultAwardValueMinor"
                            :currency-symbol="currencySymbol"
                            @input="awardValueMinor = $event"
                            @update:defaultMinor="onDefaultAwardValueMinorChange"
                          />
                          <small v-if="awardValueError" class="text-danger">{{ awardValueError }}</small>
                        </div>

                        <div v-else class="reward-amount-container">
                          <app-input
                            :value="maxAwardValue"
                            type="text"
                            inputmode="numeric"
                            :prepend="currencySymbol"
                            placeholder="e.g. 50"
                            @input="onMaxAwardValueInput"
                          >
                            <template #label>
                              <div class="label">
                                Maximum amount
                                <small>The most a nominator can award.</small>
                              </div>
                            </template>
                          </app-input>
                          <small v-if="maxAwardValueError" class="text-danger">{{ maxAwardValueError }}</small>
                        </div>
                      </template>

                      <template #after-amounts>
                        <budget-settings-section
                          v-if="monetaryRewardEnabled && !fundedFromPots"
                          :currency-symbol="currencySymbol"
                          :amount="rewardBudgetAmount"
                          :cadence="rewardBudgetCadence"
                          :year-start-month="budgetYearStartMonth"
                          :year-start-day="budgetYearStartDay"
                          :year-start-day-options="budgetYearStartDayOptions"
                          :year-start-disabled="budgetYearStartDisabled"
                          :year-start-month-rules="budgetYearStartMonthRules"
                          :year-start-day-rules="budgetYearStartDayRules"
                          @update:amount="rewardBudgetAmount = $event"
                          @update:cadence="rewardBudgetCadence = $event"
                          @update:yearStartMonth="budgetYearStartMonth = $event"
                          @update:yearStartDay="budgetYearStartDay = $event"
                        />

                        <div v-if="monetaryRewardEnabled" class="categories-container">
                          <div class="label-row">
                            <div class="label">
                              Add spend categories (optional)
                              <small>Let people tag what a reward is for, so you can track spending by category.</small>
                            </div>
                            <b-form-checkbox v-model="categoriesEnabled" switch />
                          </div>
                          <div v-if="categoriesEnabled">
                            <create-categories-modal v-model="categories" />
                            <b-form-checkbox v-model="categoryRequired" class="mt-2" :disabled="!categories.length">
                              Make selecting a category mandatory
                            </b-form-checkbox>
                            <b-form-checkbox v-model="showCategoryEnabled" class="mt-2" :disabled="!categories.length">
                              Show the category to employees in their feed
                            </b-form-checkbox>
                          </div>
                        </div>
                      </template>
                    </monetary-reward-section>
                  </div>
                </sme-card>
              </div>

              <!-- Nominations & Approvals -->
              <div>
                <h4>{{ isInstant ? 'Managers' : 'Nominations & Approvals' }}</h4>
                <sme-card large>
                  <div v-if="!isInstant" class="label-permissions-and-approvals">Permissions and approvals</div>

                  <div v-if="!isInstant" class="nomination-type-container">
                    <app-input
                      v-model="nominationMode"
                      :validate-immediate="false"
                      name="Nomination type"
                      undefined-label="Select who can nominate"
                      type="dropdown"
                      class="mb-0"
                      :options="nominationOptions"
                    >
                      <template #label>
                        <div class="label">Who can nominate?</div>
                      </template>
                    </app-input>

                    <multi-select
                      v-if="nominationMode === NOMINATION_MODES.CLOSED"
                      id="nominators-select"
                      :value="selectedNominatorIds"
                      :options="employeeOptions"
                      placeholder="Select who can nominate for this award"
                      :searchable="true"
                      search-placeholder="Search by name or email"
                      :disabled="employeesLoading || divisionEmployeePairsLoading"
                      :select-all="false"
                      select-all-text="No nominators available"
                      class="mb-0"
                      @input="onNominatorsChange"
                    />
                    <small v-if="nominatorSelectionError" class="text-danger">{{ nominatorSelectionError }}</small>
                  </div>

                  <div class="approver-container">
                    <app-input
                      v-if="!isInstant && managerRulesAvailable"
                      v-model="approverMode"
                      :validate-immediate="false"
                      name="Approver type"
                      type="dropdown"
                      class="mb-3"
                      :options="approverModeOptions"
                    >
                      <template #label>
                        <div class="label">Who can approve?</div>
                      </template>
                    </app-input>
                    <multi-select
                      id="approvers-select"
                      :value="selectedApproverIds"
                      :options="allEmployeeOptions"
                      :placeholder="isInstant ? 'Select who can give this award' : 'Select who can approve this award'"
                      :searchable="true"
                      search-placeholder="Search by name or email"
                      :disabled="employeesLoading"
                      :select-all="false"
                      :select-all-text="isInstant ? 'No managers available' : 'No approvers available'"
                      class="mb-0"
                      @input="onApproversChange"
                    >
                      <template #label>
                        <div class="label">
                          {{ approverPickerLabel }}
                          <small v-if="isInstant"> Selected managers give this award directly from the app. </small>
                          <small v-else-if="approverMode !== APPROVER_MODES.PEOPLE">
                            Used when the approver chosen above can't be found in the HR data.
                          </small>
                        </div>
                      </template>
                    </multi-select>
                    <small v-if="approverSelectionError" class="text-danger">{{ approverSelectionError }}</small>
                    <manager-budgets-section
                      v-if="monetaryRewardEnabled && fundedFromPots"
                      :manager-ids="selectedApproverIds"
                      :pots="pots"
                      :employees="employees"
                      :loading="potsLoading"
                    />
                  </div>
                </sme-card>
              </div>
            </div>
          </b-form>
        </ValidationObserver>
      </b-col>
    </template>

    <template #footer>
      <div class="d-flex align-items-center justify-content-between w-100 px-4 py-3">
        <b-button variant="outline-secondary" :disabled="saving" @click="$router.back()">Cancel</b-button>
        <div class="d-flex align-items-center">
          <small v-if="errorMessage" class="text-danger mr-3">{{ errorMessage }}</small>
          <b-button type="submit" variant="primary" :disabled="saving" form="create-award-form">
            <b-spinner v-if="saving" class="mr-2" small />
            {{ saving ? 'Creating...' : 'Create award' }}
          </b-button>
        </div>
      </div>
    </template>
  </app-page>
</template>

<script setup>
import { ValidationObserver } from 'vee-validate';
import { computed, onBeforeMount, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router/composables';
import ApiClient, { apiErrorData } from '@/ApiClient';
import {
  APPROVER_MODES,
  APPROVER_MODE_OPTIONS,
  NOMINATION_MODES,
  NOMINATION_MODE_OPTIONS,
  RECOGNITION_AWARD_TYPES,
  RECOGNITION_APPROVAL_TYPES,
  RECOGNITION_PROGRAM_AWARD_MODES,
  approverRuleForMode,
  isInstantProgramAwardMode,
  nominatorRuleForMode,
} from '@/pages/recognition/constants';
import { useAwardForm } from '@/pages/recognition/composables/useAwardForm';
import { useScopedEmployeeFiltering } from '@/pages/recognition/composables/useScopedEmployeeFiltering';
import AppInput from '@/components/AppInput.vue';
import AppPage from '@/components/AppPage.vue';
import MultiSelect from '@/components/MultiSelect.vue';
import useCompanySwitcher from '@/state/composables/useCompanySwitcher';
import ActiveCompanyPill from '@/pages/recognition/components/ActiveCompanyPill.vue';
import CreateCompanyValuesModal from '@/pages/recognition/components/CreateCompanyValuesModal.vue';
import CreateCategoriesModal from '@/pages/recognition/components/CreateCategoriesModal.vue';
import CreateRewardAmountsModal from '@/pages/recognition/components/CreateRewardAmountsModal.vue';
import useToast from '@/composables/useToast';
import SmeCard from '@/components/atoms/SmeCard.vue';
import BasicDetailsSection from '@/pages/recognition/components/BasicDetailsSection.vue';
import ImageUploadSection from '@/pages/recognition/components/ImageUploadSection.vue';
import MonetaryRewardSection from '@/pages/recognition/components/MonetaryRewardSection.vue';
import BudgetSettingsSection from '@/pages/recognition/components/BudgetSettingsSection.vue';
import ManagerBudgetsSection from '@/pages/recognition/components/budgets/ManagerBudgetsSection.vue';
import { useBudgetPots } from '@/pages/recognition/composables/useBudgetPots';

const { shouldShowCompanyIndicator } = useCompanySwitcher();

const $route = useRoute();
const awardMode = computed(() =>
  $route.query.mode === RECOGNITION_PROGRAM_AWARD_MODES.INSTANT
    ? RECOGNITION_PROGRAM_AWARD_MODES.INSTANT
    : RECOGNITION_PROGRAM_AWARD_MODES.NOMINATE,
);
const isInstant = computed(() => isInstantProgramAwardMode(awardMode.value));
const pageTitle = computed(() => (isInstant.value ? 'Create an instant award' : 'Create a nominated award'));
const pageSubtitle = computed(() =>
  isInstant.value
    ? 'Set the basics, choose which managers can give it, and personalise the award card.'
    : 'Set the basics, choose who can nominate and approve, and personalise the award card.',
);

// Use shared composable
const {
  currencySymbol,
  awardForm,
  scope,
  scopeFields,
  programScopePayload,
  requiresExplicitDivisionScope,
  divisionScopeError,
  initDivisionScope,
  validateDivisionScope,
  monetaryRewardEnabled,
  amountMode,
  awardValueMinor,
  awardValueError,
  maxAwardValue,
  maxAwardValueError,
  defaultAwardValueMinor,
  selectedRedemptionOptions,
  redemptionSelectionError,
  expirationMonths,
  rewardBudgetAmount,
  rewardBudgetCadence,
  budgetYearStartMonth,
  budgetYearStartDay,
  budgetYearStartDayOptions,
  budgetYearStartDisabled,
  budgetYearStartMonthRules,
  budgetYearStartDayRules,
  categoriesEnabled,
  categories,
  categoryRequired,
  showCategoryEnabled,
  shortDescription,
  bannerImages,
  bannerImagesError,
  allowNominatorImageChoice,
  addBannerImages,
  addPendingBannerFiles,
  removeBannerImage,
  badgeFile,
  badgePreviewUrl,
  allowedImageTypes,
  MAX_BANNER_SIZE_BYTES,
  onBadgeFileUpdate,
  onBadgePreviewUrlUpdate,
  onBadgeGallerySelect,
  saving,
  errorMessage,
  onMaxAwardValueInput,
  onDefaultAwardValueMinorChange,
  loadScopeFields,
  initBudgetYearStart,
  setupCommonWatchers,
  resetMonetaryRewardFields,
  buildBaseProperties,
  getImageData,
  getBudgetFields,
  budgetsEnabled,
  getGrantExpiryInterval,
  persistBudgetYearStart,
  validateRedemptionOptions,
  validateAwardValues,
  validateBudgetCadence,
} = useAwardForm();

const fundedFromPots = computed(() => budgetsEnabled.value && isInstant.value);
const { pots, isLoading: potsLoading } = useBudgetPots(fundedFromPots);

// Division-based employee filtering
const {
  employees,
  employeesLoading,
  divisionEmployeePairsLoading,
  eligibleEmployeeIds,
  employeeOptions,
  allEmployeeOptions,
  refreshEligibleEmployeeIds,
  loadEmployeesAndDivisionPairs,
} = useScopedEmployeeFiltering(scope);

// Program-specific state
const badgesEnabled = ref(true);
const companyValuesEnabled = ref(false);
const companyValues = ref([]);
const selectedApproverIds = ref([]);
const selectedNominatorIds = ref([]);
const nominationMode = ref(NOMINATION_MODES.OPEN);
const approverMode = ref(APPROVER_MODES.PEOPLE);
const managerRulesAvailable = ref(false);
const approverModeOptions = APPROVER_MODE_OPTIONS;
const nominationOptions = computed(() =>
  NOMINATION_MODE_OPTIONS.filter(o => managerRulesAvailable.value || o.value !== NOMINATION_MODES.MANAGER),
);
const approverPickerLabel = computed(() => {
  if (isInstant.value) {
    return 'Who can give this award?';
  }
  return approverMode.value === APPROVER_MODES.PEOPLE ? 'Who can approve?' : 'Fallback approvers (optional)';
});
const nominatorSelectionError = ref('');
const approverSelectionError = ref('');

onBeforeMount(async () => {
  await Promise.all([loadScopeFields(), loadManagerRulesAvailable(), ...loadEmployeesAndDivisionPairs()]);
  initDivisionScope();
  initBudgetYearStart();
});

const $router = useRouter();
const { doToast, TOAST_VARIANT } = useToast();

async function loadManagerRulesAvailable() {
  try {
    managerRulesAvailable.value = Boolean((await ApiClient.getRecognitionCompanyInfo())?.manager_rules_available);
  } catch (e) {
    managerRulesAvailable.value = false;
  }
}

// Setup common watchers
setupCommonWatchers();

const buildProperties = (approverIds, redemptionMethods, nominators) => {
  const props = buildBaseProperties(redemptionMethods);

  props.approvers = (approverIds || []).length
    ? [
        {
          name: 'Default',
          approvers: (approverIds || []).map(String),
        },
      ]
    : undefined;

  props.award_mode = awardMode.value;

  if (!isInstant.value && Array.isArray(nominators) && nominators.length > 0) {
    props.nominators = (nominators || []).map(String);
  }
  if (!isInstant.value) {
    const nominatorRule = nominatorRuleForMode(nominationMode.value);
    const approverRule = approverRuleForMode(approverMode.value);
    if (nominatorRule) {
      props.nominator_rule = nominatorRule;
    }
    if (approverRule) {
      props.approver_rule = approverRule;
    }
  }

  if (companyValuesEnabled.value) {
    const values = (companyValues.value || []).map(v => String(v || '').trim()).filter(Boolean);
    if (values.length) {
      props.company_values = values.map(name => ({ name }));
    }
  }

  return props;
};

const buildPayload = properties => ({
  program_type: RECOGNITION_AWARD_TYPES.PROGRAM,
  program_name: awardForm.value.name,
  approval_type: RECOGNITION_APPROVAL_TYPES.ALL,
  show_badge: badgesEnabled.value,
  ...programScopePayload.value,
  ...getBudgetFields({ hasOwnBudget: !fundedFromPots.value }),
  grant_expiry_interval: getGrantExpiryInterval(),
  properties,
});

const onSubmit = async () => {
  errorMessage.value = '';
  saving.value = true;
  const approvers = selectedApproverIds.value || [];
  const redemptionMethods = monetaryRewardEnabled.value ? selectedRedemptionOptions.value || [] : [];
  const nominators = nominationMode.value === NOMINATION_MODES.CLOSED ? selectedNominatorIds.value || [] : [];

  try {
    if (!validateDivisionScope()) {
      saving.value = false;
      return;
    }

    if (!validateRedemptionOptions(redemptionMethods)) {
      saving.value = false;
      return;
    }

    if ((isInstant.value || approverMode.value === APPROVER_MODES.PEOPLE) && approvers.length === 0) {
      approverSelectionError.value = isInstant.value
        ? 'Please select at least one manager.'
        : 'Please select at least one approver.';
      saving.value = false;
      return;
    }
    approverSelectionError.value = '';

    if (!isInstant.value && nominationMode.value === NOMINATION_MODES.CLOSED && nominators.length === 0) {
      nominatorSelectionError.value = 'Please select at least one nominator when closed nominations are selected.';
      saving.value = false;
      return;
    }
    nominatorSelectionError.value = '';

    if (!validateAwardValues()) {
      saving.value = false;
      return;
    }

    if (!validateBudgetCadence()) {
      saving.value = false;
      return;
    }

    const approverIds = (approvers || []).map(String);
    const properties = buildProperties(approverIds, redemptionMethods, nominators);
    const payload = buildPayload(properties);

    const budgetResult = await persistBudgetYearStart();
    if (!budgetResult.success) {
      errorMessage.value = budgetResult.error;
      saving.value = false;
      return;
    }

    const { files, s3Keys } = getImageData();

    await ApiClient.createRecognitionProgram(payload, files, s3Keys);

    doToast('Success', isInstant.value ? 'Instant award created' : 'Nominated award created', TOAST_VARIANT.SUCCESS);
    $router.push({ name: 'recognition' });
  } catch (e) {
    errorMessage.value = apiErrorData(e).message || e?.message || 'Failed to create award';
  } finally {
    saving.value = false;
  }
};

const onScopeChange = async newScope => {
  scope.value = newScope;
  await refreshEligibleEmployeeIds();
  const allowed = eligibleEmployeeIds.value || new Set();
  // Approvers can be any company employee (HERO-2974); only nominators are scoped.
  selectedNominatorIds.value = (selectedNominatorIds.value || []).filter(id => allowed.has(id));
};

const onNominatorsChange = ids => {
  selectedNominatorIds.value = ids;
};

const onApproversChange = ids => {
  selectedApproverIds.value = ids;
};

watch(selectedNominatorIds, newVal => {
  if (nominatorSelectionError.value && Array.isArray(newVal) && newVal.length > 0) {
    nominatorSelectionError.value = '';
  }
});

watch(selectedApproverIds, newVal => {
  if (approverSelectionError.value && Array.isArray(newVal) && newVal.length > 0) {
    approverSelectionError.value = '';
  }
});

watch(nominationMode, mode => {
  if (mode !== NOMINATION_MODES.CLOSED) {
    selectedNominatorIds.value = [];
    nominatorSelectionError.value = '';
  }
});

watch(approverMode, () => {
  approverSelectionError.value = '';
});

watch(monetaryRewardEnabled, enabled => {
  if (!enabled) {
    resetMonetaryRewardFields();
  }
});
</script>

<style scoped lang="scss">
@import '@/pages/recognition/styles/award-form';
</style>
